/**
 * PowerupTracker — Triple-A HUD element showing the player's active
 * powerup effects as animated chips.
 *
 * Each chip displays:
 *   - Icon (from the powerup's sprite asset)
 *   - Carved stone + gold frame backdrop (matching game style)
 *   - Countdown ring drawing the remaining time
 *   - Scale pop-in on activation, fade-out on expiry
 *
 * Usage:
 *   const tracker = PowerupTracker.get(scene);
 *   tracker.setAnchor(x, y);             // Upper-right below ball counter
 *   tracker.addChip('shield', 'powerup_shield', 5000);
 *   tracker.addChip('rapidfire', 'powerup_rapidfire', 8000);
 *   tracker.removeChip('shield');       // Immediate remove (e.g., shield popped)
 *
 * The tracker draws chips horizontally from the anchor, left-to-right.
 */

import Phaser from 'phaser';

interface Chip {
  id: string;
  container: Phaser.GameObjects.Container;
  ring: Phaser.GameObjects.Graphics;
  startTime: number;
  durationMs: number;
  expiryTimer?: Phaser.Time.TimerEvent;
  updateHandler?: (time: number) => void;
}

export class PowerupTracker {
  private static instances = new WeakMap<Phaser.Scene, PowerupTracker>();

  static get(scene: Phaser.Scene): PowerupTracker {
    let inst = this.instances.get(scene);
    if (!inst) {
      inst = new PowerupTracker(scene);
      this.instances.set(scene, inst);
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        inst!.destroy();
        this.instances.delete(scene);
      });
    }
    return inst;
  }

  private scene: Phaser.Scene;
  private anchorX = 1800;
  private anchorY = 165;
  private chipSize = 72;
  private chipSpacing = 10;
  private chips: Map<string, Chip> = new Map();
  private destroyed = false;

  private constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  setAnchor(x: number, y: number): void {
    this.anchorX = x;
    this.anchorY = y;
    this.relayout();
  }

  /**
   * Add a new active powerup chip. Auto-removes itself after durationMs.
   * If a chip with the same id already exists, it is refreshed.
   */
  addChip(id: string, iconKey: string, durationMs: number): void {
    if (this.destroyed || !this.scene?.sys?.isActive()) return;

    // If exists, refresh duration
    if (this.chips.has(id)) {
      const existing = this.chips.get(id)!;
      existing.startTime = this.scene.time.now;
      existing.durationMs = durationMs;
      if (existing.expiryTimer) existing.expiryTimer.remove();
      existing.expiryTimer = this.scene.time.delayedCall(durationMs, () => this.removeChip(id));
      return;
    }

    const container = this.scene.add.container(0, 0);
    container.setDepth(150);
    container.setScrollFactor(0);

    const size = this.chipSize;

    // ─── Carved stone backdrop ──────────────────────────────────────
    const bg = this.scene.add.graphics();
    // Outer shadow
    bg.fillStyle(0x000000, 0.5);
    bg.fillRoundedRect(-size / 2 + 3, -size / 2 + 3, size, size, 12);
    // Main stone
    bg.fillStyle(0x1a2a3e, 0.92);
    bg.fillRoundedRect(-size / 2, -size / 2, size, size, 12);
    // Inner highlight
    bg.fillStyle(0x243d58, 0.8);
    bg.fillRoundedRect(-size / 2 + 4, -size / 2 + 4, size - 8, size - 8, 10);
    // Gold border
    bg.lineStyle(3, 0xFFD700, 1);
    bg.strokeRoundedRect(-size / 2, -size / 2, size, size, 12);
    bg.lineStyle(1, 0xFFE880, 0.7);
    bg.strokeRoundedRect(-size / 2 + 2, -size / 2 + 2, size - 4, size - 4, 10);
    container.add(bg);

    // ─── Icon sprite ─────────────────────────────────────────────────
    if (this.scene.textures.exists(iconKey)) {
      const icon = this.scene.add.image(0, 0, iconKey);
      // Fit icon to ~60% of chip
      const target = size * 0.6;
      const scale = Math.min(target / icon.width, target / icon.height);
      icon.setScale(scale);
      container.add(icon);
    } else {
      // Fallback: first letter of id
      const fallback = this.scene.add.text(0, 0, id.substring(0, 1).toUpperCase(), {
        fontSize: '36px',
        fontFamily: 'CF Asty Std',
        color: '#FFD700',
        stroke: '#000000',
        strokeThickness: 4,
      });
      fallback.setOrigin(0.5);
      container.add(fallback);
    }

    // ─── Countdown ring (drawn on top) ──────────────────────────────
    const ring = this.scene.add.graphics();
    container.add(ring);

    const chip: Chip = {
      id,
      container,
      ring,
      startTime: this.scene.time.now,
      durationMs,
    };
    this.chips.set(id, chip);

    // Subscribe to update for ring drawing
    chip.updateHandler = () => this.drawRing(chip);
    this.scene.events.on('update', chip.updateHandler);

    // Schedule expiry
    chip.expiryTimer = this.scene.time.delayedCall(durationMs, () => this.removeChip(id));

    // Entrance pop animation
    container.setScale(0);
    this.scene.tweens.add({
      targets: container,
      scale: 1,
      duration: 350,
      ease: 'Back.easeOut',
      easeParams: [3],
    });

    // Layout
    this.relayout();
  }

  /**
   * Remove a chip immediately (used when shield pops early, etc).
   */
  removeChip(id: string): void {
    const chip = this.chips.get(id);
    if (!chip) return;

    if (chip.expiryTimer) chip.expiryTimer.remove();
    if (chip.updateHandler) this.scene.events.off('update', chip.updateHandler);

    // Exit animation
    this.scene.tweens.add({
      targets: chip.container,
      scale: 0,
      alpha: 0,
      duration: 250,
      ease: 'Back.easeIn',
      onComplete: () => {
        chip.container.destroy();
        this.chips.delete(id);
        this.relayout();
      },
    });
  }

  /**
   * Remove all chips (called when player dies or stage ends).
   */
  clear(): void {
    for (const id of Array.from(this.chips.keys())) {
      this.removeChip(id);
    }
  }

  private drawRing(chip: Chip): void {
    if (!chip.ring || !chip.ring.active) return;
    const elapsed = this.scene.time.now - chip.startTime;
    const remaining = Math.max(0, chip.durationMs - elapsed);
    const pct = Math.max(0, Math.min(1, remaining / chip.durationMs));

    chip.ring.clear();
    // Draw arc from top clockwise — fades as time passes
    const radius = this.chipSize / 2 - 4;
    const startAngle = -Math.PI / 2;
    const endAngle = startAngle + Math.PI * 2 * pct;

    // Color shifts as time runs out: gold → orange → red
    let color = 0xFFD700;
    if (pct < 0.3) color = 0xFF3333;
    else if (pct < 0.6) color = 0xFF8822;

    chip.ring.lineStyle(4, color, 0.95);
    chip.ring.beginPath();
    chip.ring.arc(0, 0, radius, startAngle, endAngle, false);
    chip.ring.strokePath();

    // Pulse scale on the whole container when <1s remains
    if (remaining < 1000 && remaining > 0) {
      const pulse = 1 + Math.sin(this.scene.time.now / 80) * 0.08;
      chip.container.setScale(pulse);
    }
  }

  /**
   * Relayout all chips horizontally from the anchor (right-aligned).
   * Most-recently-added chip is leftmost.
   */
  private relayout(): void {
    const ids = Array.from(this.chips.keys());
    ids.forEach((id, index) => {
      const chip = this.chips.get(id)!;
      const targetX = this.anchorX - index * (this.chipSize + this.chipSpacing);
      const targetY = this.anchorY;
      // Tween to new position
      this.scene.tweens.add({
        targets: chip.container,
        x: targetX,
        y: targetY,
        duration: 200,
        ease: 'Power2',
      });
    });
  }

  private destroy(): void {
    this.destroyed = true;
    for (const chip of this.chips.values()) {
      if (chip.expiryTimer) chip.expiryTimer.remove();
      if (chip.updateHandler) {
        try { this.scene.events.off('update', chip.updateHandler); } catch {}
      }
      chip.container?.destroy();
    }
    this.chips.clear();
  }
}
